import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { useDispatch, useSelector } from "react-redux";
import { getProductDataRequest } from "../../redux/Products/productSlice";
import calculateFinalPrice from "../../Utils/product_price_calculator";
import { useNavigate } from "react-router-dom";
import {
  getBullionRatesRequest,
  getAppConfigRequest,
} from "../../redux/Dashboard/dashboardSlice";
import CarouselSlider from "../CarouselSection/CarouselSlider";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const NO_CONFIG_TEXT =
  "Great things take time. We are building something special for you! A golden feature is on it's way.✨";
const COMING_SOON_TEXT = "COMING SOON...";

function ImageFallback({ src, alt, height = 180 }) {
  const [err, setErr] = useState(false);

  const defaultImg = "/images/logo.png";

  return (
    <img
      src={err || !src ? defaultImg : src}
      alt={alt || "product image"}
      style={{
        width: "100%",
        height: "auto",
        maxHeight: height,
        aspectRatio: "1 / 1",
        objectFit: "cover",
        display: "block",
      }}
      onError={() => setErr(true)}
    />
  );
}

export default function ProductView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bullionRates, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );
  const { carousel } = useSelector((state) => state.dashboard || {});
  const productState = useSelector((s) => s.product);
  const { isLoading, data } = productState || {};

  const [currentBudId, setCurrentBudId] = useState(null);
  const [currentTitle, setCurrentTitle] = useState("Collections");
  const [budsList, setBudsList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [topCarousel, setTopCarousel] = useState(null);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  const isLookbookEnabled = appConfig?.featureToggle?.lookbook || appConfig?.featureToggle?.look_book === true;

  useEffect(() => {
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(getAppConfigRequest());
  }, [dispatch]);

  useEffect(() => {
    if (isLookbookEnabled) {
      dispatch(getProductDataRequest());
    }
  }, [dispatch, isLookbookEnabled]);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      const lookbook = carousel.data.find((c) => c.name === "lookbook") || null;

      if (lookbook) {
        setTopCarousel({
          slides: lookbook.slides,
          height: parseFloat(lookbook.height) || 200,
          showNavigator: lookbook.shownavigator,
          duration: lookbook.duration,
          enabled: lookbook.enabled,
        });
      } else {
        setTopCarousel(null);
      }
    } else {
      setTopCarousel(null);
    }
  }, [carousel]);

  // useEffect(() => {
  //   console.log("FULL PRODUCT API RESPONSE →", data);
  // }, [data]);

  const budsModel = useMemo(() => {
    if (!data) {
      return { buds: [], products: [] };
    }

    const payload = data;

    const products =
      Array.isArray(payload.product_object) && payload.product_object.length
        ? payload.product_object.map((p) => ({
            ...p,
            id: p._id,
            images: (p.url || []).map((img) =>
              !img || typeof img !== "string"
                ? ""
                : img.startsWith("http")
                  ? img
                  : `${process.env.REACT_APP_CDN_URL}/${process.env.REACT_APP_PACKAGE_NAME}/${img.replace(/^\//, "")}`,
            ),
            metalWeight: p.metal_weight,
            purity: p.purity,
            bullion: p.bullion || {},
            productCharges: p.productCharges || {},
            taxPercentage: p.taxPercentage,
          }))
        : [];

    const buds =
      Array.isArray(payload.all_category) && payload.all_category.length
        ? payload.all_category.map((b) => ({
            ...b,
            id: b._id,
            name: b.name,
            parentId: b.parent_id || "",
            products: b.products || [],
            isLive: b.is_live,
            position: b.position || 0,
            image: b.image || "",
          }))
        : [];

    return {
      buds,
      products,
    };
  }, [data]);

  const getRootBudId = (buds) => {
    const root = buds.find((b) => !b.parentId || b.parentId === "");
    return root ? root.id : null;
  };

  useEffect(() => {
    if (!budsModel.buds.length) return;
    const root = getRootBudId(budsModel.buds);
    if (root) {
      setCurrentBudId((prev) => prev || root);
      setCurrentTitle("Collections");
      loadBuds(root, budsModel);
    }
  }, [budsModel.buds.length]);

  function loadBuds(parentId, model = budsModel) {
    const bud = model.buds.find((b) => b.id === parentId) || null;

    const children = model.buds
      .filter((b) => b.parentId === parentId && b.isLive)
      .sort((a, b) => (b.position || 0) - (a.position || 0));

    const collectProducts = (budId) => {
      const b = model.buds.find((x) => x.id === budId);
      let ids = b?.products || [];

      const sub = model.buds.filter((x) => x.parentId === budId);
      sub.forEach((child) => {
        ids = [...ids, ...(child.products || [])];
        ids = [...ids, ...collectProducts(child.id)];
      });

      return ids;
    };

    const allProductIds = collectProducts(parentId);
    const uniqueIds = [...new Set(allProductIds)];

    const products = model.products.filter((p) => uniqueIds.includes(p.id));

    setBudsList(children);
    setProductList(products);
  }

  function handleBudClick(bud) {
    setCurrentBudId(bud.id);
    setCurrentTitle(bud.name || "Collection");
    loadBuds(bud.id);
  }

  function handleBack() {
    const root = getRootBudId(budsModel.buds);
    if (!currentBudId || currentBudId === root) {
      setCurrentBudId(root);
      setCurrentTitle("Collections");
      loadBuds(root);
      return;
    }

    const parentBud = budsModel.buds.find((b) => b.id === currentBudId);
    const parentId = parentBud ? parentBud.parentId : null;
    if (parentId) {
      setCurrentBudId(parentId);
      const parent = budsModel.buds.find((b) => b.id === parentId);
      setCurrentTitle(parent ? parent.name : "Collections");
      loadBuds(parentId);
    } else {
      const rootId = getRootBudId(budsModel.buds);
      setCurrentBudId(rootId);
      setCurrentTitle("Collections");
      loadBuds(rootId);
    }
  }

  function findMetalRateForProduct(product) {
    if (!product || !product.bullion) return 0;

    const productBullionId =
      product.bullion._id || product.bullion.id || product.bullion;

    if (!productBullionId) return 0;

    const rateObj = (bullionRates || []).find(
      (r) =>
        r.rateType === "Buy" &&
        r.bullion &&
        (r.bullion._id === productBullionId ||
          r.bullion.id === productBullionId),
    );

    return rateObj ? Number(rateObj.ratePerGram || 0) : 0;
  }

  if (!appConfig) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isLookbookEnabled) {
    return (
      <Box
        sx={{
          background: themeColors.scaffoldGradientColors,
          minHeight: "100vh",
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ background: themeColors.appBar }}
        >
          <Toolbar>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, color: themeColors.primary }}
            >
              Collections
            </Typography>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 5,
            minHeight: "60vh",
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: themeColors.primary }}>
            {NO_CONFIG_TEXT}
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: themeColors.primary, fontWeight: "bold" }}
          >
            {COMING_SOON_TEXT}
          </Typography>
        </Box>
      </Box>
    );
  }

  // 3. products loading
  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!budsModel.buds.length && !budsModel.products.length) {
    return (
      <Box textAlign="center" p={4}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ background: themeColors.appBar }}
      >
        <Toolbar>
          {currentTitle !== "Collections" && (
            <IconButton onClick={handleBack}>
              <ArrowBackIcon sx={{ color: themeColors.primary }} />
            </IconButton>
          )}

          <Typography
            variant="h6"
            sx={{ flexGrow: 1, color: themeColors.primary }}
          >
            {currentTitle}
          </Typography>

          <IconButton onClick={() => navigate("/productOrder")}>
            <ShoppingBagIcon sx={{ color: themeColors.primary }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container sx={{ py: 3 }}>
        {topCarousel?.enabled && (
          <Box mb={3}>
            <CarouselSlider
              slides={topCarousel.slides}
              height={topCarousel.height}
              showNavigator={topCarousel.showNavigator}
              duration={topCarousel.duration}
              enabled={topCarousel.enabled}
            />
          </Box>
        )}
        {budsList.length > 0 && (
          <Grid container spacing={3} mb={3}>
            {budsList.map((bud) => (
              <Grid size={{ xs: 6, md: 3 }} key={bud.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleBudClick(bud)}
                >
                  <ImageFallback src={bud.image} alt={bud.name} height={160} />
                  <CardContent
                    sx={{
                      textAlign: "center",
                      background: themeColors.mediumContainer,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color="white"
                      sx={{ fontWeight: 600, color: themeColors.text }}
                    >
                      {bud.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {productList.length === 0 ? (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", mt: 5, color: "grey" }}
          >
            No Products Found
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {productList.map((product) => {
              const metalRate = findMetalRateForProduct(product);
              const priceObj = calculateFinalPrice({
                metalRate,
                purity: product.bullion?.purity || product.purity || 999,
                inputPurity: product.purity || 999,
                weight: product.metalWeight || product.metal_weight || 0,
                productCharges: product.productCharges || {},
                taxPercentage: product.taxPercentage || 0,
              });

              return (
                <Grid size={{ xs: 6, md: 3 }} key={product.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/productDetails", {
                        state: { product, priceObj },
                      })
                    }
                  >
                    <ImageFallback
                      src={product.images?.[0]}
                      alt={product.name}
                      height={200}
                    />

                    <CardContent
                      sx={{
                        background: themeColors.mediumContainer,
                        color: themeColors.text,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        noWrap
                        sx={{ mb: 1 }}
                      >
                        {product.name}
                      </Typography>

                      <Typography variant="h6">
                        {symbol}{" "}
                        {Number(priceObj.finalPrice).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
